import { useParams } from "react-router-dom";
import AppLeftSideBar from "../../components/SideBar/AppLeftSideBar";
import {
  createInstanceAction,
  createPromoterInstance,
  getMetrics,
  getPromoterConfig,
  getPromoterInstances,
  updatePromoterConfig,
  updatePromoterInstance
} from "../../services/apis/releaseApis";
import useQuery from "../../hooks/useQuery";
import ErrorMessage from "../../components/FormComponents/ErrorMessage";
import Loader from "../../Layouts/Loader/Loader";
import PipelineConfigCard from "./components/PipelineConfigCard";
import { useMemo, useState } from "react";
import BuilderRightSideBar from "../../Layouts/BuilderRightSideBar";
import PipelineConfigSettings from "./components/PipelineConfigSettings";
import { toastError, toastSuccess } from "../../utils/toastUtils";
import CreateReleaseSidebar from "./components/CreateReleaseSidebar";
import ReleaseInstancesList from "./components/ReleaseInstanceList/ReleaseInstancesList";
import Pagination from "../ApplicationUsers/Pagination";
import ReleaseFilters from "./components/ReleaseFilters";
import Button from "../../components/FormComponents/Button";
import { useModalContext } from "../../context/ModalContext";

const PROM_CONFIG_KEY = ["promoter-config"];
const METRICS_KEY = ["metrics"];

const ReleasesViewer = () => {
  const { appId } = useParams();
  const [openSettings, setOpenSettings] = useState(false);
  const [openCreateRelease, setOpenCreateRelease] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10 });
  const [filters, setFilters] = useState({ status: "", org_id: "", app_id: "" });
  const { getUserConfirmation } = useModalContext();
  const { data, isLoading, error, refetch } = useQuery(PROM_CONFIG_KEY, ({ signal }) => getPromoterConfig({ signal }), {
    onError: (err) => console.error("Promoter config error:", err)
  });
  const config = data?.data;

  const { data: metricsData, refetch: refetchMetrics } = useQuery(METRICS_KEY, ({ signal }) => getMetrics({ signal }));

  const instancesQueryKey = useMemo(
    () => ["promoter-instances", pagination.page, pagination.limit, filters.app_id, filters.org_id, filters.status],
    [pagination.page, pagination.limit, filters.org_id, filters.app_id, filters.status]
  );

  const {
    data: instancesData,
    isLoading: isLoadingInstances,
    refetch: refetchInstances
  } = useQuery(instancesQueryKey, ({ signal }) =>
    getPromoterInstances({
      signal,
      params: {
        page: pagination.page,
        limit: pagination.limit,
        app_id: filters.app_id,
        org_id: filters.org_id,
        status: filters.status
      }
    })
  );

  const instances = Array.isArray(instancesData?.data?.data)
    ? instancesData.data.data
    : Array.isArray(instancesData?.data)
      ? instancesData.data
      : [];

  const totalPages = instancesData?.data?.total_pages || 1;
  const pageTrack = useMemo(
    () => ({
      currPage: pagination.page,
      totalPages
    }),
    [pagination.page, totalPages]
  );

  const handleUpdateConfig = async (payload) => {
    try {
      await updatePromoterConfig(payload);
      refetch();
      setOpenSettings(false);
      toastSuccess("Configuration updated");
    } catch (err) {
      toastError("Update failed");
      throw err;
    }
  };

  const handleCreateRelease = async (payload) => {
    await createPromoterInstance(payload);
    refetchInstances();
    setOpenCreateRelease(false);
    toastSuccess("Release Created Successfully");
  };

  const handleAction = async (instanceId, payload) => {
    try {
      await createInstanceAction(instanceId, payload);
      toastSuccess(`Action ${payload.action} successful`);
      refetchInstances();
    } catch (err) {
      console.error(err);
      toastError("Action failed");
    }
  };

  const handlePromote = async (id) => {
    try {
      const userConfirmed = await getUserConfirmation(`Are You Sure, You want to Promote?`);
      if (!userConfirmed) return;
      await updatePromoterInstance(id);
      toastSuccess(`Promote successful`);
      refetchInstances();
    } catch (err) {
      console.error(err);
      toastError("Promote failed");
    }
  };

  return (
    <div className="application_container d_flex h_100 w_100 overflow_hidden">
      <AppLeftSideBar type={appId ? "app" : "org"} />

      <div className="flex_grow h_100 overflow_hidden p_3 px_4">
        <div className="d_flex justify_between align_center mb_4">
          <h2 className="m_0 font_weight_bold font_size_xlarge">Releases</h2>
        </div>

        {isLoading && (
          <div className="d_flex justify_center align_center h_100">
            <Loader />
          </div>
        )}

        {error && (
          <ErrorMessage className="d_flex flex_column justify_center align_center gap_2 w_100">
            <p className="m_0 font_size_small opacity_75 mt_1">{error?.message}</p>
            <Button onClick={refetch} variant="secondary">
              Retry
            </Button>
          </ErrorMessage>
        )}

        {config && (
          <div className="d_flex flex_column gap_2 h_100 overflow_hidden">
            <div
              style={{
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                padding: "16px"
              }}
            >
              <div className="flex_shrink_0 pb_2">
                <PipelineConfigCard
                  config={config}
                  metrics={metricsData?.data}
                  onToggleSettings={() => setOpenSettings(true)}
                />
              </div>

              <div className="flex_shrink_0" style={{ borderTop: "1px solid #E5E7EB" }}>
                <ReleaseFilters
                  filters={filters}
                  onFilterChange={(newFilters) => {
                    setFilters(newFilters);
                    setPagination((prev) => ({ ...prev, page: 1 }));
                  }}
                  onToggleRelease={() => setOpenCreateRelease(true)}
                  onRefresh={() => {
                    refetchInstances();
                    refetchMetrics();
                  }}
                />
              </div>
            </div>

            <div className="flex_grow overflow_auto d_flex flex_column">
              <div className="flex_grow">
                <ReleaseInstancesList
                  instances={instances}
                  isLoading={isLoadingInstances}
                  onAction={handleAction}
                  onPromote={handlePromote}
                />
              </div>

              {instances.length > 0 && (
                <div className="flex_shrink_0 mt_3 pb_2">
                  <Pagination
                    pageTrack={pageTrack}
                    pagination={pagination}
                    onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
                    onLimitChange={(limit) => setPagination((prev) => ({ ...prev, limit, page: 1 }))}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <BuilderRightSideBar isOpen={openSettings} onClose={setOpenSettings} heading="Release Settings" size="small">
        <PipelineConfigSettings config={config} onClose={setOpenSettings} onSubmit={handleUpdateConfig} />
      </BuilderRightSideBar>
      <CreateReleaseSidebar isOpen={openCreateRelease} onClose={setOpenCreateRelease} onSubmit={handleCreateRelease} />
    </div>
  );
};

export default ReleasesViewer;
